import { Link, useLocation } from 'react-router-dom';
import { Clock, Mic, FileText, Bot, Settings, ListTodo, BookA, Calendar, CalendarDays, BarChart3, Package, Briefcase, Users, ClipboardList } from 'lucide-react';
import type { AppModuleManifest } from '../modules/types';
import type { ReactNode } from 'react';
import { useAuth } from '@onvice/portal-shell-api';
import { isOverviewMode } from '../lib/overviewMode';

interface Props {
  manifest: AppModuleManifest;
  children: ReactNode;
}

const ICON_MAP: Record<string, typeof Clock> = {
  'Zeiten': Clock,
  'Heute': Calendar,
  'Sprache': Mic,
  'Berichte': FileText,
  'Aufgaben': ListTodo,
  'Kalender': CalendarDays,
  'Mein Glossar': BookA,
  'Glossar-Verwaltung': BookA,
  'Auswertungen': BarChart3,
  'Material-Stamm': Package,
  'Material-Verbrauch': Package,
  'Übersicht': ClipboardList,
  'Kunden': Briefcase,
  'Mitarbeiter': Users,
  'Assistent': Bot,
  'Einstellungen': Settings,
};

export default function ModuleLayout({ manifest, children }: Props) {
  const location = useLocation();
  const { isAdmin, user, hasPermission } = useAuth();
  const basePath = `/m/${manifest.slug}`;
  const navRoutes = manifest.routes.filter((r) => {
    if (r.navigation === 'main') {
      if (isAdmin) return true;
      if (r.permission) return hasPermission(r.permission);
      if (Array.isArray(r.roles)) return !!user?.role && r.roles.includes(user.role);
      return true;
    }
    if (r.navigation !== 'settings') return false;
    if (isAdmin) return true;
    if (r.permission && hasPermission(r.permission)) return true;
    return Array.isArray(r.roles) && !!user?.role && r.roles.includes(user.role);
  });

  const isActive = (routePath: string) => {
    const fullPath = routePath === '/' ? basePath : `${basePath}${routePath}`;
    if (routePath === '/') return location.pathname === basePath || location.pathname === basePath + '/';
    return location.pathname.startsWith(fullPath);
  };

  // Wenn der User aus der Uebersicht-Sicht kommt, blendet ModuleLayout seine
  // eigene Sidebar + BottomNav aus — die Uebersicht-Tab-Bar oben uebernimmt
  // die Navigation und wuerde sich sonst mit der Modul-Sidebar doppeln.
  const hideModuleNav = isOverviewMode();

  // Joerg 2026-06-14: BottomNav nur rendern wenn das Modul tatsaechlich
  // mobile Navigation-Items hat. Sonst (z.B. chat mit Single-Route ohne
  // navigation:'main') klebt ein leerer 80px-bg-slate-900-Streifen am
  // Viewport-Boden und versteckt fixed-Composer dahinter.
  // Ausnahme: Modul setzt usesBottomBarSlot=true -> BottomBar wird leer
  // gerendert, Modul fuellt sie via createPortal (chat-Composer).
  const mobileRoutes = navRoutes.filter((r) => !r.hideOnMobile);
  const wantsBottomBarSlot = manifest.usesBottomBarSlot === true;
  // 2026-07-13 (Joerg): Bei genau einer Mobile-Route (z.B. Plantafel) hat die
  // BottomNav einen einzigen Button ohne Effekt und verdeckt nur Anzeigeflaeche.
  // Weglassen — es sei denn ein Modul reserviert die Zeile via usesBottomBarSlot
  // (chat-Composer). Analog zu showTopTabs oben.
  const showBottomNav = !hideModuleNav && (mobileRoutes.length > 1 || wantsBottomBarSlot);

  // 2026-07-06 (Joerg): Umbau von Modul-Sidebar links auf horizontale Tab-Bar
  // oben, damit sie nicht mit der Portal-Sidebar konkurriert und keine 192px
  // Content-Breite frisst. Bei genau einer navRoute (Vorlagen, Chat u.a.) wird
  // gar keine Nav gerendert — der Modul-Kopf reicht als Orientierung.
  const showTopTabs = !hideModuleNav && navRoutes.length > 1;

  return (
    <div style={{ minHeight: 'calc(100vh - 56px)' }}>
      {/* Desktop: horizontale Tab-Bar oberhalb des Contents (statt der frueheren
          linken Sidebar). Sticky unter Portal-Header (h-14) + evtl. UebersichtTabBar.
          Auf Mobile bleibt die BottomNav wie vorher. */}
      {showTopTabs && (
        <div className="hidden lg:block sticky top-14 z-20 bg-card/95 backdrop-blur border-b border-default">
          <nav className="max-w-7xl mx-auto flex items-center gap-1 h-11 px-4 overflow-x-auto">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-3 flex-shrink-0">
              {manifest.name}
            </span>
            {navRoutes.map((route) => {
              const Icon = ICON_MAP[route.label || ''] || FileText;
              const active = isActive(route.path);
              return (
                <Link
                  key={route.path}
                  to={route.path === '/' ? basePath : `${basePath}${route.path}`}
                  style={active ? {
                    color: 'var(--color-primary)',
                    borderBottomColor: 'var(--color-primary)',
                  } : undefined}
                  className={`inline-flex items-center gap-1.5 px-3 h-11 text-sm border-b-2 transition-colors flex-shrink-0 ${
                    active ? 'font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon size={14} />
                  {route.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Content */}
      <div className={`min-w-0 ${showBottomNav ? 'pb-20 lg:pb-0' : ''}`}>
        {children}
      </div>

      {/* Mobile: BottomNav (in Uebersicht-Mode unterdrueckt). hideOnMobile-Items
          werden hier ausgefiltert — z.B. in BV2 sind Aufgaben+Berichte auf Mobile
          ausgeblendet, weil Dashboard+Kalender alles abdecken. Items sind grosszuegig
          gross (HIG-konform >= 60×64px) damit Touch-Treffer auf der Baustelle klappen.
          Breakpoint lg statt sm — Handy-Querformat soll dieselbe reduzierte Nav haben.
          z-40 (nicht z-50): Modals nutzen z-50 und sollen ueber der BottomNav
          liegen, sonst verschwindet der Modal-Footer dahinter (Joerg/Alex+Kiara
          2026-05-27, Re-Test #11). */}
      {showBottomNav && wantsBottomBarSlot && mobileRoutes.length === 0 && (
        // Leerer Slot fuer Module die per createPortal befuellen (chat).
        // Joerg 2026-07-24: min-h-20 statt h-20 + flex-col justify-end — der
        // Composer waechst nach oben statt in einen fixen 80px-Slot gequetscht
        // zu werden. Chat-InboxPage misst die tatsaechliche Slot-Hoehe und
        // schrumpft den Message-Stream dynamisch nach.
        <div
          data-module-bottombar-slot
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-900 border-t border-slate-800 min-h-20 flex flex-col justify-end safe-area-bottom"
        />
      )}
      {showBottomNav && mobileRoutes.length > 0 && (
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-900 border-t border-slate-800 safe-area-bottom">
          <div className="flex items-center justify-around h-20 max-w-2xl mx-auto px-2">
            {mobileRoutes.map((route) => {
              const Icon = ICON_MAP[route.label || ''] || FileText;
              const active = isActive(route.path);
              return (
                <Link
                  key={route.path}
                  to={route.path === '/' ? basePath : `${basePath}${route.path}`}
                  style={active ? {
                    color: 'var(--color-primary)',
                    backgroundColor: 'color-mix(in srgb, var(--color-primary) 12%, transparent)',
                  } : undefined}
                  className={`flex flex-col items-center justify-center gap-1 min-w-[68px] min-h-[64px] px-2 py-1 rounded-xl transition-colors ${
                    active ? '' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Icon size={26} />
                  <span className="text-[11px] font-medium leading-tight">{route.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
